import React, { useState } from "react";
// components
import { Card, Grid, Typography, Button, Box } from "@mui/material";
import AppDialog from "../../components/AppDialog";
// services
import { useQueryClient } from "@tanstack/react-query";
// constants
import { ROLE_NAMES, THEME_COLOR3 } from "../../constants";
// icons
import { ReactComponent as DeleteIcon } from "../../assets/images/delete1.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { roleServices } from "../../api/useQueryHooks";

const RoleCard = (props) => {
  const [showPermissions, setShowPermissions] = useState(false);
  const [appDialogState, setAppDialogState] = useState({ open: false });

  const queryClient = useQueryClient();

  const deleteRoleMutation = roleServices.useDeleteRole(
    () => {
      props.setMsgAlert({
        open: true,
        message: "Role deleted successfully!",
        msgType: "success",
      });
      queryClient.invalidateQueries("getAllRoles");
      closeAppDialog();
    },
    () => {
      props.setMsgAlert({
        open: true,
        message: "Error deleting role",
        msgType: "error",
      });
    }
  );

  const handlePermissionViewClick = () => {
    setShowPermissions(!showPermissions);
  };

  const handleEditClick = () => {
    props.setPopupState({ type: "edit", data: props });
  };

  const handleDeleteClick = (roleName) => {
    setAppDialogState({
      open: true,
      title: (
        <Box>
          <Typography sx={{ margin: 0 }}>
            <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
          >
            Delete Role?
          </Typography>
        </Box>
      ),
      content: (
        <Box>
          Are you sure you want to delete the role "{roleName}"? This action
          cannot be undone.
        </Box>
      ),
      firstBtnText: "Cancel",
      secondBtnText: "Delete",
      onClose: closeAppDialog,
      onConfirm: () => {
        deleteRoleMutation.mutate(props._id);
      },
    });
  };

  const closeAppDialog = () => setAppDialogState({ open: false });

  return (
    <>
      <Card sx={styles.mainGrid}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Role Name */}
          <Typography
            sx={{
              fontFamily: "GilroyBold",
              fontSize: 14,
              color: "black",
              paddingTop: 0.8,
              paddingBottom: 0.8,
            }}
          >
            {props.name}
          </Typography>

          {/* Buttons */}
          <Grid
            flex={1}
            container
            direction="row"
            justifyContent="flex-end"
            alignItems={"center"}
            gap={2}
          >
            <Box onClick={() => handleEditClick()}>
              <EditIcon width={24} height={24} />
            </Box>
            {props.name !== ROLE_NAMES.superAdmin && (
              <Box onClick={() => handleDeleteClick(props.name)}>
                <DeleteIcon width={24} height={24} />
              </Box>
            )}
            <Button
              variant="contained"
              sx={styles.viewButton}
              onClick={handlePermissionViewClick}
            >
              {showPermissions ? "Hide Permissions " : "View Permission"}
            </Button>
          </Grid>
        </Grid>

        {/* Permission Count */}
        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: 12,
            color: THEME_COLOR3,
            marginTop: 1,
          }}
        >
          Total Permissions: {props.permissions?.length || 0}
        </Typography>

        {/* Permissions List */}
        {showPermissions && (
          <Grid container direction="column" sx={{ marginTop: 1 }}>
            <Typography
              sx={{
                fontFamily: "GilroyBold",
                fontSize: 12,
                color: THEME_COLOR3,
                marginBottom: 1,
              }}
            >
              Permissions:
            </Typography>
            <Grid container spacing={1} padding={2}>
              {props.permissions?.map((permission, index) => (
                <Grid item key={index} xs={12} sm={6} md={4}>
                  <Box
                    item
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      padding: 0.5,
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      backgroundColor: "#f9f9f9",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "GilroySemiBold",
                        fontSize: 12,
                        color: "black",
                      }}
                    >
                      {permission.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "GilroyRegular",
                        fontSize: 10,
                        color: "#666",
                      }}
                    >
                      Type: {permission.type}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <AppDialog
        open={appDialogState.open}
        onClose={appDialogState.onClose}
        content={appDialogState.content}
        title={appDialogState.title}
        firstBtnContent={appDialogState.firstBtnText}
        secondBtnContent={
          deleteRoleMutation?.isPending
            ? "Please wait..."
            : appDialogState.secondBtnText
        }
        firstBtnClickHandler={appDialogState.onClose}
        secondBtnClickHandler={appDialogState.onConfirm}
        options={{ btnLoading: deleteRoleMutation?.isPending }}
      />
    </>
  );
};

export default RoleCard;

const styles = {
  mainGrid: {
    cursor: "pointer",
    borderRadius: 3,
    width: "100%",
    marginBottom: 2,
    padding: 1.3,
  },
  viewButton: {
    backgroundColor: "#000",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 11,
    "&:hover": {
      backgroundColor: "#000000",
    },
  },
  cancelButton: {
    color: "#000",
    fontFamily: "GilroyBold",
    fontSize: 12,
    "&:hover": {
      backgroundColor: "#1565c0",
      color: "#FFF",
    },
  },
  confirmDelete: {
    backgroundColor: "#000",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 12,
    marginRight: 1,
    "&:hover": {
      backgroundColor: "#c62828",
      color: "#FFF",
    },
  },
};
